import { useMemo, useState } from 'react';

export type BookingDayStatus = 'open' | 'closed' | 'no_first_timer';

export type BookingDay = {
  date: string; // YYYY-MM-DD
  status: BookingDayStatus;
};

const WEEKDAYS = ['日', '月', '火', '水', '木', '金', '土'];

// "YYYY-MM" の月を n か月ずらす
const shiftMonth = (ym: string, n: number): string => {
  const [y, m] = ym.split('-').map(Number);
  const d = new Date(Date.UTC(y, m - 1 + n, 1));
  return d.toISOString().slice(0, 7);
};

// YYYY-MM-DD を「M月D日（曜）」に整形する
export const formatDateJa = (yyyymmdd: string): string => {
  const [y, m, d] = yyyymmdd.split('-').map(Number);
  const wd = new Date(Date.UTC(y, m - 1, d)).getUTCDay();
  return `${m}月${d}日（${WEEKDAYS[wd]}）`;
};

/**
 * 予約できる日だけを押せる月カレンダー。days（翌日から数か月分の受付可否）に open で
 * 入っている日だけが選べ、店休日・初回受付不可の日・範囲外の日は押せない。
 * 月の切り替えは days の範囲の月だけ。
 */
export function DateCalendar(props: {
  days: BookingDay[];
  value: string;
  onChange: (date: string) => void;
}) {
  const { days, value, onChange } = props;
  const open = useMemo(
    () => new Set(days.filter(d => d.status === 'open').map(d => d.date)),
    [days],
  );
  const firstMonth = days[0]?.date.slice(0, 7) ?? '';
  const lastMonth = days[days.length - 1]?.date.slice(0, 7) ?? '';
  // 最初に開く月: 選択済みならその月、無ければ最初に予約できる日の月
  const [ym, setYm] = useState(
    () => (value || days.find(d => d.status === 'open')?.date || days[0]?.date || '').slice(0, 7),
  );

  const [y, m] = ym.split('-').map(Number);
  const firstDow = new Date(Date.UTC(y, m - 1, 1)).getUTCDay();
  const daysInMonth = new Date(Date.UTC(y, m, 0)).getUTCDate();
  const cells = [
    ...Array.from({ length: firstDow }, (_, i) => <span key={`b${i}`} className="cal-cell blank" />),
    ...Array.from({ length: daysInMonth }, (_, i) => {
      const date = `${ym}-${String(i + 1).padStart(2, '0')}`;
      const wd = (firstDow + i) % 7;
      const selectable = open.has(date);
      const cls = [
        'cal-cell',
        wd === 0 ? 'sun' : wd === 6 ? 'sat' : '',
        selectable ? 'open' : 'off',
        date === value ? 'selected' : '',
      ].filter(Boolean).join(' ');
      return (
        <button
          key={date}
          type="button"
          className={cls}
          disabled={!selectable}
          aria-pressed={date === value}
          aria-label={`${formatDateJa(date)}${selectable ? '' : ' 予約不可'}`}
          onClick={() => onChange(date)}
        >
          {i + 1}
        </button>
      );
    }),
  ];

  return (
    <div className="cal">
      <div className="cal-nav">
        <button
          type="button"
          onClick={() => setYm(shiftMonth(ym, -1))}
          disabled={ym <= firstMonth}
          aria-label="前の月"
        >‹</button>
        <span className="cal-title">{y}年{m}月</span>
        <button
          type="button"
          onClick={() => setYm(shiftMonth(ym, 1))}
          disabled={ym >= lastMonth}
          aria-label="次の月"
        >›</button>
      </div>
      <div className="cal-grid">
        {WEEKDAYS.map((w, i) => (
          <span key={w} className={`cal-dow${i === 0 ? ' sun' : i === 6 ? ' sat' : ''}`}>{w}</span>
        ))}
        {cells}
      </div>
      <p className="cal-note">※ 選択できない日は、店休日または初回のご予約を承っていない日です。</p>
    </div>
  );
}
